import API from '../config/api';
import axios from 'axios';
/*
  Read the stored login without letting a bad value take the page down.
  A half-written or cleared userInfo makes JSON.parse throw, which would
  otherwise crash every caller — including render paths.
*/
export function readStoredUser() {
  try {
    return JSON.parse(localStorage.getItem('userInfo') || 'null');
  } catch (e) {
    console.warn('userInfo in localStorage is not valid JSON — ignoring it.');

    return null;
  }
}

/*
  A stable id for the current visitor. Logged-in users are keyed by their
  _id; guests get a persisted random id so their dwell is tracked too.
*/
export function getVisitorId() {
  const userInfo = readStoredUser();

  if (userInfo?._id) {
    return userInfo._id;
  }

  let anon = localStorage.getItem('anonVisitorId');

  if (!anon) {
    anon =
      'anon-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem('anonVisitorId', anon);
  }

  return anon;
}

/*
  Send a completed dwell to the backend. Prefers navigator.sendBeacon so
  the request survives tab close / navigation; falls back to axios.
*/
export function sendProductView({ productId, duration, source }) {
  if (!productId || !duration || duration < 1) {
    return;
  }

  const userInfo = readStoredUser();

  const payload = {
    visitorId: getVisitorId(),
    userId: userInfo?._id || null,
    productId,
    duration: Math.round(duration),
    source: source || 'details'
  };

  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(payload)], {
        type: 'application/json'
      });
      if (navigator.sendBeacon(API, blob)) {
        return;
      }
    }
  } catch (e) {
    /* fall through to axios */
  }

  axios.post(API, payload).catch(() => {});
}

/*
  Call right after login: re-attributes any guest browsing done in this
  browser to the now-logged-in user, so admin sees their real name and
  their guest dwell feeds their personalized suggestions.
*/
export async function claimGuestHistory(userId) {
  const anonVisitorId = localStorage.getItem('anonVisitorId');

  if (!anonVisitorId || !userId) {
    return;
  }

  try {
    await axios.post(`${API}/claim`, {
      anonVisitorId,
      userId
    });
    /* Future views now key off userId; drop the stale anon id */
    localStorage.removeItem('anonVisitorId');
  } catch (e) {
    /* non-critical — ignore */
  }
}

/*
  Fetch time-based suggestions for the current visitor.

  Failures used to be swallowed silently, which made the home-page section
  vanish with nothing to go on. Anything unexpected is now reported.
*/
export async function fetchTimeRecommendations() {
  const visitorId = getVisitorId();

  try {
    const { data } = await axios.get(`${API}/recommendations/${visitorId}`);

    if (!Array.isArray(data)) {
      console.error('Suggestions: expected an array, got', data);

      return [];
    }

    return data;
  } catch (e) {
    console.error(
      `Suggestions: request to ${API}/recommendations/${visitorId} failed —`,
      e.message
    );

    return [];
  }
}
